import { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useChannels, usePipelineJobs, useStreamSummaries, useStreams } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { Badge } from 'src/components/ui/Badge';
import { Icon } from 'src/components/ui/Icon';
import { Skeleton } from 'src/components/ui/Skeleton';
import { buildJobBoard, MACHINE_COPY } from 'src/lib/jobBoard';
import { formatAgo, formatDateTime, formatDayLong, formatMeridiem, mexicoCityDayKey } from 'src/lib/time';
import type { Tone } from 'src/lib/tone';
import type { EditorialStream, EditorialStreamStatus, EditorialStreamSummary } from 'src/types/editorial';
import { LoadFailure } from 'src/components/editorial/LoadFailure';

/** Answers: which broadcast do I work on? (Composition §1) */

/* The page-size choices every console table offers — powers of ten times the default row count,
   so a reader who outgrows 10 can land on 25, 50 or 100 without a custom value. */
const PAGE_SIZES = [10, 25, 50, 100] as const;

const STATUS_TONE = {
  live: 'contradicted',
  reprocessing: 'partial',
  completed: 'supported',
  error: 'contradicted',
} satisfies Record<EditorialStreamStatus, Tone>;

const STATUS_LABEL = {
  live: 'en vivo',
  reprocessing: 'reprocesando',
  completed: 'completa',
  error: 'con error',
} satisfies Record<EditorialStreamStatus, string>;

const CONTENT_FILTER = {
  all: 'Todas',
  stories: 'Con historias',
  stopped: 'Detenidas antes de las historias',
} as const;

type ContentFilter = keyof typeof CONTENT_FILTER;

export function BroadcastsWorkspace() {
  const { data, isPending, error, refetch } = useStreams();

  return (
    <div className="page-hero">
      <header className="landmark broadcasts-head">
        <div>
          <p className="thread__eyebrow">Centro de monitoreo</p>
          <h1 className="section-title">Transmisiones</h1>
          <p className="relevance__note">
            Revisa lo que se dijo en vivo y abre una transmisión para recorrer sus etapas.
          </p>
        </div>
        <HeadDate />
      </header>

      {isPending && <Skeleton variant="rows" count={6} />}

      {error && (
        <LoadFailure title="No se pudieron cargar las transmisiones" error={error} onRetry={refetch} />
      )}

      {data && data.streams.length === 0 && (
        <>
          <CaptureStatusCard streams={data.streams} />
          <AbsenceState
            kind="empty"
            what="No hay transmisiones registradas."
            why="El pipeline no ha ingerido ninguna conferencia todavía."
            wouldChange="Corre `pressentia-editorial monitor <canal>` o `ingest <url>` para producir la primera."
          />
        </>
      )}

      {data && data.streams.length > 0 && (
        <>
          <CaptureStatusCard streams={data.streams} />
          <BroadcastIndex streams={data.streams} />
        </>
      )}
    </div>
  );
}

/** The newsroom's clock, top right of the header — a monitoring center answers "what day is
 *  it" before "what happened". Ticks every half minute so the meridiem never goes stale. */
function HeadDate() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  return (
    <p className="broadcasts-head__date mono">
      <span>{formatDayLong(now)}</span>
      <span>{formatMeridiem(now)}</span>
    </p>
  );
}

/**
 * The capture status as an operations card: machine state on the left, the three figures an
 * editor checks first on the right. "Transmisiones hoy" counts the loaded list — the index
 * caps at 50 rows, so a day with more captures than that undercounts here.
 */
function CaptureStatusCard({ streams }: { streams: EditorialStream[] }) {
  // The same queue the rail reads — a list of broadcasts is where an editor first asks "is the
  // machine even on?".
  const jobs = usePipelineJobs({ limit: 25 });
  const channels = useChannels();
  const board = jobs.data ? buildJobBoard(jobs.data.jobs, Date.now()) : null;
  const machine = board ? MACHINE_COPY[board.machine] : null;
  const activeSources = channels.data?.channels.filter(channel => channel.active).length;

  const todayKey = mexicoCityDayKey(new Date().toISOString());
  const todayCount = streams.filter(stream => mexicoCityDayKey(stream.createdAt) === todayKey).length;
  const processing = board ? board.running.length + board.queued.length : null;
  const newest = streams.reduce<string | null>((latest, stream) => {
    if (!latest || Date.parse(stream.createdAt) > Date.parse(latest)) return stream.createdAt;
    return latest;
  }, null);

  return (
    <section className="capture-card" role="status" aria-label="Estado de la captura">
      <div className="capture-card__identity">
        <p className="capture-card__title">
          <i className="capture-card__dot" data-tone={machine?.tone ?? 'unresolved'} aria-hidden="true" />
          Procesamiento automático
          {machine && <Badge tone={machine.tone}>{machine.label}</Badge>}
        </p>
        <p className="capture-card__detail">
          {machine?.detail ?? 'Consultando el estado del procesamiento.'}
        </p>
        <p className="capture-card__sources">
          Fuentes activas <strong>{activeSources ?? '…'}</strong>
        </p>
      </div>
      <dl className="capture-card__stats">
        <div className="capture-card__stat">
          <dt>Transmisiones hoy</dt>
          <dd>{todayCount}</dd>
        </div>
        <div className="capture-card__stat">
          <dt>En procesamiento</dt>
          <dd>{processing ?? '…'}</dd>
        </div>
        <div className="capture-card__stat">
          <dt>Última captura</dt>
          <dd>{formatAgo(newest, new Date())}</dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * A flat index, newest first, one row per broadcast, behind one filter card. The day grouping the
 * roster used made sense when the list was short enough to read as diary; as an index of work, a
 * column of dates scans faster than a stack of day bands, and one table takes filters without
 * explaining which band the answer fell into. Fifty broadcasts in, the pager keeps the first
 * screen a decision instead of a scroll.
 */
function BroadcastIndex({ streams }: { streams: EditorialStream[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [content, setContent] = useState<ContentFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>(10);
  const searchId = useId();
  const contentId = useId();
  const fromId = useId();
  const toId = useId();
  const sizeId = useId();

  // One aggregate per broadcast, cached under the same key the stream screens use — so opening a
  // broadcast from here finds its counts already loaded.
  const { byStream } = useStreamSummaries(streams.map(stream => stream.streamId));

  const needle = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      streams.filter(stream => {
        if (needle && !stream.title.toLowerCase().includes(needle) && !stream.streamId.toLowerCase().includes(needle)) {
          return false;
        }
        const day = mexicoCityDayKey(stream.createdAt);
        if (from && day < from) return false;
        if (to && day > to) return false;
        if (content === 'all') return true;
        const summary = byStream.get(stream.streamId);
        // A summary still in flight is not a "no" — it keeps the row under "Todas" but out of a
        // verdict it cannot honestly render yet.
        if (!summary) return false;
        const stopped = summary.prenotas === 0 && summary.turns > 0;
        return content === 'stopped' ? stopped : summary.prenotas > 0;
      }),
    [streams, needle, from, to, content, byStream]
  );

  // Any change to the filters re-answers the question from the first page; clamping instead of
  // resetting would leave the pager pointing at a page that may no longer exist. Resizing the
  // page itself does the same — the slice the new size shows starts back at the top.
  useEffect(() => {
    setPage(1);
  }, [needle, content, from, to, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, totalPages);
  const pageRows = filtered.slice((current - 1) * pageSize, current * pageSize);

  const clear = () => {
    setQuery('');
    setContent('all');
    setFrom('');
    setTo('');
  };

  return (
    <>
      <section className="filters-card" aria-label="Filtros del listado de transmisiones">
        <div className="filters-card__field filters-card__field--grow">
          <label htmlFor={searchId}>Buscar</label>
          <div className="search-box">
            <Icon name="search" />
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Buscar por nombre de transmisión"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="filters-card__field filters-card__field--select">
          <label htmlFor={contentId}>Contenido</label>
          <select id={contentId} value={content} onChange={event => setContent(event.target.value as ContentFilter)}>
            {(Object.keys(CONTENT_FILTER) as ContentFilter[]).map(value => (
              <option key={value} value={value}>
                {CONTENT_FILTER[value]}
              </option>
            ))}
          </select>
        </div>
        <div className="filters-card__field filters-card__field--date">
          <label htmlFor={fromId}>Desde</label>
          <input id={fromId} type="date" value={from} onChange={event => setFrom(event.target.value)} />
        </div>
        <div className="filters-card__field filters-card__field--date">
          <label htmlFor={toId}>Hasta</label>
          <input id={toId} type="date" value={to} onChange={event => setTo(event.target.value)} />
        </div>
        <button type="button" className="filters-card__clear" onClick={clear}>
          Limpiar
        </button>
      </section>

      <div className="board-table__scroll">
        {pageRows.length === 0 ? (
          <p className="table-empty">Sin resultados para los filtros aplicados</p>
        ) : (
          <table className="board-table board-table--broadcasts">
            <caption className="visually-hidden">Transmisiones registradas</caption>
            <thead>
              <tr>
                <th scope="col">Transmisión</th>
                <th scope="col">Fecha</th>
                <th scope="col" className="board-table__num">
                  Historias
                </th>
                <th scope="col" className="board-table__num">
                  Borradores
                </th>
                <th scope="col">Estado</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(stream => {
                const summary = byStream.get(stream.streamId);
                const open = () => navigate(`/stream/${encodeURIComponent(stream.streamId)}`);
                return (
                  <tr
                    key={stream.streamId}
                    className="board-table__row"
                    role="link"
                    tabIndex={0}
                    aria-label={`Abrir ${stream.title || stream.streamId}`}
                    onClick={open}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        open();
                      }
                    }}
                  >
                    <td>
                      <p className="board__headline">{stream.title || stream.streamId}</p>
                      <StreamNote summary={summary} />
                    </td>
                    <td className="board-table__date">{formatDateTime(stream.createdAt)}</td>
                    <td className="board-table__num">{summary ? countOrDash(summary.prenotas) : '…'}</td>
                    <td className="board-table__num">{summary ? countOrDash(summary.outputs) : '…'}</td>
                    <td>
                      <Badge tone={STATUS_TONE[stream.status]}>{STATUS_LABEL[stream.status]}</Badge>
                      <span className="board__open" aria-hidden="true">
                        →
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <nav className="pager" aria-label="Paginación del listado">
        <div className="pager__meta">
          <span className="pager__info" aria-live="polite">
            {filtered.length === 0
              ? 'Sin resultados'
              : `Mostrando ${(current - 1) * pageSize + 1}–${Math.min(current * pageSize, filtered.length)} de ${filtered.length}`}
          </span>
          <label className="pager__size" htmlFor={sizeId}>
            <span className="visually-hidden">Elementos por página</span>
            <select
              id={sizeId}
              value={pageSize}
              onChange={event => setPageSize(Number(event.target.value) as (typeof PAGE_SIZES)[number])}
            >
              {PAGE_SIZES.map(size => (
                <option key={size} value={size}>
                  {size} por página
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="pager__controls">
          <button
            type="button"
            className="pager__btn"
            disabled={current <= 1}
            onClick={() => setPage(current - 1)}
          >
            <Icon name="chevronLeft" />
            Anterior
          </button>
          {pageNumbers(current, totalPages).map((item, index) =>
            item === '…' ? (
              <span key={`gap-${index}`} className="pager__gap" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className="pager__btn"
                aria-current={item === current ? 'page' : undefined}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            )
          )}
          <button
            type="button"
            className="pager__btn"
            disabled={current >= totalPages}
            onClick={() => setPage(current + 1)}
          >
            Siguiente
            <Icon name="chevronRight" />
          </button>
        </div>
      </nav>
    </>
  );
}

/** A broadcast that stopped early says where it stopped instead of showing a row of zeros. */
function StreamNote({ summary }: { summary: EditorialStreamSummary | undefined }) {
  if (!summary) return null;

  if (summary.prenotas === 0 && summary.turns > 0) {
    return <p className="board__note">Se detuvo antes de las historias · {summary.turns} turnos procesados</p>;
  }
  if (summary.turns === 0) {
    return <p className="board__note">Sin transcribir</p>;
  }
  return null;
}

/** Zero is real for a finished broadcast, so it stays a number; the dash is for "no llegó". */
function countOrDash(count: number): string {
  return count > 0 ? `${count}` : '—';
}

/** 1-based page list with ellipses: `1 … 4 5 6 … 12`. Small page counts stay whole. */
function pageNumbers(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

  const pages = new Set<number>([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter(page => page >= 1 && page <= total).sort((a, b) => a - b);

  const out: Array<number | '…'> = [];
  let previous = 0;
  for (const page of sorted) {
    if (page - previous > 1) out.push('…');
    out.push(page);
    previous = page;
  }
  return out;
}
